// Workspace state — manages the active admin workspace (`production` vs
// `sandbox`) and exposes it to the admin API client + topbar switcher.
//
// Why localStorage instead of React state alone? The admin API helper
// (`lib/admin-api.js`) needs to read the current workspace synchronously
// when building requests, including from places outside the React tree
// (e.g. queued network calls fired during route transitions). Storing the
// selection in localStorage keeps the source of truth synchronous and
// resilient across reloads.
//
// Demo admins are hard-locked to sandbox in the backend regardless of
// what the frontend asks for — the value here is purely a UX hint.

const KEY = 'rb-admin-workspace';
export const PRODUCTION = 'production';
export const SANDBOX = 'sandbox';
const ALLOWED = new Set([PRODUCTION, SANDBOX]);

export const SANDBOX_LOCKED_EMAILS = new Set([
  'aarav.mehta@example.com',
]);

export function isSandboxLocked(email) {
  if (!email) return false;
  return SANDBOX_LOCKED_EMAILS.has(email.trim().toLowerCase());
}

export function getWorkspace() {
  try {
    const v = localStorage.getItem(KEY);
    return ALLOWED.has(v) ? v : PRODUCTION;
  } catch (_) {
    return PRODUCTION;
  }
}

export function setWorkspace(ws) {
  const next = ALLOWED.has(ws) ? ws : PRODUCTION;
  try {
    localStorage.setItem(KEY, next);
    // Notify listeners in the same tab (the `storage` event only fires
    // on OTHER tabs, so we use a custom event for the current one).
    window.dispatchEvent(new CustomEvent('rb-workspace-changed', { detail: next }));
  } catch (_) { /* ignore */ }
  return next;
}

/**
 * Resolve the effective workspace for a given user. Demo-locked emails
 * always operate in `sandbox` regardless of localStorage; everyone else
 * gets the explicit user choice (defaulting to `production`).
 */
export function effectiveWorkspace(user) {
  if (!user) return getWorkspace();
  if (isSandboxLocked(user.email)) return SANDBOX;
  return getWorkspace();
}
